import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * This hook will execute "call" function every time it's updated and handle async meta state for it.
 * @param call - Memoized function to be called from hook. "useCallback" hook is a preferred way to wrap it.
 * @param initialData - Initial data that will be returned from hook as "data" parameter
 * @deprecated use more sofisticated solutions, such as react-query
 */
export const useAPI = <T>(call: () => Promise<{ value: T }>, initialData: T) => {
    const [data, setData] = useState(initialData);
    const [isLoading, setIsLoading] = useState(false);
    const [hasFailed, setHasFailed] = useState(false);
    const unmountState = useRef(false);

    const fetchData = useCallback(async () => {
        if (unmountState.current === false) {
            setHasFailed(false);
            setIsLoading(true);
        }

        try {
            const { value } = await call();

            if (unmountState.current === false) {
                setData(value);
            }
        } catch (error) {
            if (unmountState.current === false) {
                setHasFailed(true);
            }
        }

        if (unmountState.current === false) {
            setIsLoading(false);
        }
    }, [call]);

    useEffect(() => {
        unmountState.current = false;

        void fetchData();

        return () => {
            unmountState.current = true;
        };
    }, [fetchData]);

    return { data, isLoading, hasFailed, fetchData };
};
